<template>
    <SaveView :loading="saving" :disabled="!hasChangesFull" :title="$t(`86f953db-fdce-4be5-abc2-bbedaf4ac63a`)" @save="save" v-on="!isNew ? {delete: deleteMe} : {}">
        <h1 v-if="isNew">
            {{ $t('c5602934-95a8-437d-b576-eda8d9e6565e') }}
        </h1>
        <h1 v-else>
            {{ $t('ed3e84f0-7ab3-467b-948f-0423ccbf2056') }}
        </h1>

        <STErrorsDefault :error-box="errors.errorBox" />
        <STInputBox error-fields="name" :error-box="errors.errorBox" :title="$t(`a3e11da3-1894-4519-99a8-ac5fb32c555d`)">
            <input ref="firstInput" v-model="name" class="input" type="text" autocomplete="off" :placeholder="$t(`23302106-6c68-4b4a-8d48-6a7c0debdeb8`)">
        </STInputBox>
        <p class="style-description-small">
            {{ $t('9b4f3b56-e869-4442-bb5f-156c2b9271b0') }}
        </p>

        <EmailInput v-model="emailAddress" :validator="errors.validator" :title="$t(`7400cdce-dfb4-40e7-996b-4817385be8d8`)" :placeholder="$t(`70c99ed2-97be-4252-b7c4-6cd46d5c9513`)" />

        <STInputBox class="max">
            <STList>
                <STListItem v-if="!isDefault" :selectable="true" element-name="button" @click="isDefault = true">
                    <template #left>
                        <IconContainer icon="email-filled" class="">
                            <template #aside>
                                <span class="icon attachment stroke small" />
                            </template>
                        </IconContainer>
                    </template>

                    <h3 class="style-title-list">
                        {{ $t('b3dc156b-410d-4d3f-bd5c-5aa6f9c9e355') }}
                    </h3>
                    <p class="style-description-small">
                        {{ $t('9e99d02f-4498-4160-877d-dc14bbadc98f') }}
                    </p>

                    <template #right>
                        <span class="icon arrow-right-small gray" />
                    </template>
                </STListItem>

                <STListItem v-else :selectable="false">
                    <template #left>
                        <IconContainer icon="email-filled" class="success">
                            <template #aside>
                                <span class="icon attachment stroke small" />
                            </template>
                        </IconContainer>
                    </template>

                    <h3 class="style-title-list">
                        {{ $t('31a32fe1-8e29-41aa-99a8-bf79ab90f93b') }}
                    </h3>
                    <p class="style-description-small">
                        {{ $t('037e9189-33ef-44c1-bad2-ff3ea3da0629') }}
                    </p>
                </STListItem>

                <STListItem :selectable="true" element-name="button" @click="editPermissions">
                    <template #left>
                        <IconContainer icon="privacy" class="">
                            <template #aside>
                                <span class="icon success small" />
                            </template>
                        </IconContainer>
                    </template>

                    <h3 class="style-title-list">
                        {{ $t('4a58b661-087b-4951-9643-2b00352bba85') }}
                    </h3>
                    <p class="style-description-small">
                        {{ $t('d040c8bd-23d1-42c3-a609-5e2febc12f07') }}
                    </p>

                    <template #right>
                        <span class="icon arrow-right-small gray" />
                    </template>
                </STListItem>
            </STList>
        </STInputBox>

        <template v-if="enableMemberModule && groups.length">
            <hr>
            <h2>{{ $t('c0b96584-ebf2-452b-89e2-0bea5e63eb74') }}</h2>
            <p>
                {{ $t('a481dcc6-d66e-4098-adc4-66ae9b1ed35f') }}
            </p>

            <STList>
                <STListItem v-for="group in groups" :key="group.group.id" element-name="label" :selectable="true">
                    <template #left>
                        <Checkbox v-model="group.selected" />
                    </template>
                    <h3 class="style-title-list">
                        {{ group.group.settings.name }}
                    </h3>
                </STListItem>
            </STList>
        </template>

        <template v-if="enableWebshopModule && webshops.length">
            <hr>
            <h2>{{ $t('e85a86ee-7751-4791-984b-f67dc1106f6b') }}</h2>
            <p>
                {{ $t('6fbddfd5-c9af-492a-819f-a22370084cb6') }}
            </p>

            <STList>
                <STListItem v-for="webshop in webshops" :key="webshop.webshop.id" element-name="label" :selectable="true">
                    <template #left>
                        <Checkbox v-model="webshop.selected" />
                    </template>
                    <h3 class="style-title-list">
                        {{ webshop.webshop.meta.name }}
                    </h3>
                </STListItem>
            </STList>
        </template>
    </SaveView>
</template>

<script lang="ts" setup>
import { AutoEncoderPatchType, PartialWithoutMethods, PatchableArray, PatchableArrayAutoEncoder } from '@simonbackx/simple-encoding';
import { ComponentWithProperties, usePop, usePresent } from '@simonbackx/vue-app-navigation';
import { CenteredMessage, Checkbox, EditResourceRolesView, EmailInput, ErrorBox, IconContainer, SaveView, STErrorsDefault, STInputBox, STList, STListItem, useErrors, useOrganization, usePatchArray, usePlatform } from '@stamhoofd/components';
import { useOrganizationManager, usePatchOrganizationPeriod, usePlatformManager, useRequestOwner } from '@stamhoofd/networking';
import { Group, GroupPrivateSettings, OrganizationEmail, OrganizationPrivateMetaData, OrganizationRegistrationPeriod, PermissionsResourceType, Platform, PlatformPrivateConfig, WebshopPreview, WebshopPrivateMetaData } from '@stamhoofd/structures';
import { computed, onMounted, Ref, ref } from 'vue';

const props = defineProps<{
    email: OrganizationEmail;
    isNew: boolean;
}>();

const saving = ref(false);
const errors = useErrors();
const groups = ref([]) as Ref<SelectableGroup[]>;
const webshops = ref([]) as Ref<SelectableWebshop[]>;
const organization = useOrganization();
const platform = usePlatform();
const originalArray = computed(() => (organization.value ? organization.value.privateMeta?.emails : platform.value.privateConfig?.emails) ?? []);
const { patched: patchedArray, patch, hasChanges, addPatch: addAPatch, addPut, addArrayPatch } = usePatchArray(originalArray);
const patched = computed(() => patchedArray.value.find(e => e.id === props.email.id) ?? props.email);
const addPatch = (patch: PartialWithoutMethods<AutoEncoderPatchType<OrganizationEmail>>) => addAPatch(OrganizationEmail.patch({ id: props.email.id, ...patch }));
const organizationManager = useOrganizationManager();
const owner = useRequestOwner();
const pop = usePop();
const platformManager = usePlatformManager();
const patchOrganizationPeriod = usePatchOrganizationPeriod();
const present = usePresent();

async function editPermissions(animated = true) {
    await present({
        animated,
        adjustHistory: animated,
        modalDisplayStyle: 'popup',
        components: [
            new ComponentWithProperties(EditResourceRolesView, {
                description: $t('e01618ed-7f4f-4a80-a926-3afb7b3806fc'),
                resource: {
                    id: props.email.id,
                    name: patched.value.name || patched.value.email, description: patched.value.name ? patched.value.email : '',
                    type: PermissionsResourceType.Senders,
                },
            }),
        ],
    });
}

const name = computed({
    get: () => patched.value.name,
    set: name => addPatch({ name }),
});

const emailAddress = computed({
    get: () => patched.value.email,
    set: email => addPatch({ email }),
});

const isDefault = computed({
    get: () => patched.value.default,
    set: (d) => {
        addPatch({ default: d });

        // Remove other defaults
        if (d) {
            for (const email of patchedArray.value) {
                if (email.id !== props.email.id && email.default) {
                    addAPatch(OrganizationEmail.patch({ id: email.id, default: false }));
                }
            }
        }
        else {
            // Remove changes to default
            for (const email of patchedArray.value) {
                if (email.id !== props.email.id && email.default === false) {
                    const originalValue = originalArray.value.find(e => e.id === email.id);
                    if (originalValue && originalValue.default) {
                        addAPatch(OrganizationEmail.patch({ id: email.id, default: true }));
                    }
                }
            }
        }
    },
});

onMounted(() => {
    if (props.isNew) {
        addPut(props.email);
    }

    if (organization.value) {
        for (const group of organization.value.period.groups) {
            groups.value.push(new SelectableGroup(group, group.privateSettings !== null && group.privateSettings.defaultEmailId !== null && group.privateSettings.defaultEmailId === props.email.id));
        }

        for (const webshop of organization.value.webshops) {
            webshops.value.push(new SelectableWebshop(webshop, webshop.privateMeta !== null && webshop.privateMeta.defaultEmailId !== null && webshop.privateMeta.defaultEmailId === props.email.id));
        }
    }
});

const enableMemberModule = computed(() => {
    return organization.value?.meta.modules.useMembers ?? false;
});

const enableWebshopModule = computed(() => {
    return organization.value?.meta.modules.useWebshops ?? false;
});

class SelectableGroup {
    group: Group;
    selected = false;
    constructor(group: Group, selected = false) {
        this.selected = selected;
        this.group = group;
    }
}

class SelectableWebshop {
    webshop: WebshopPreview;
    selected = false;
    constructor(webshop: WebshopPreview, selected = false) {
        this.selected = selected;
        this.webshop = webshop;
    }
}

async function deleteMe() {
    if (saving.value) {
        return;
    }
    if (!await CenteredMessage.confirm($t('6c837cc0-1832-4bb4-830f-838ebf9911f6'), $t('eee720f3-5e00-429c-a847-cb3d4e237e4d'), $t('2c9ef19d-3838-4e9a-bb9b-e5f9a714fe4c'))) {
        return;
    }
    saving.value = true;

    const arr: PatchableArrayAutoEncoder<OrganizationEmail> = new PatchableArray();
    arr.addDelete(props.email.id);

    try {
        if (organization.value) {
            const organizationPatch = organizationManager.value.getPatch().patch({
                privateMeta: OrganizationPrivateMetaData.patch({
                    emails: arr,
                }),
            });

            await organizationManager.value.patch(organizationPatch, { owner, shouldRetry: false });
        }
        else {
            await platformManager.value.patch(Platform.patch({
                privateConfig: PlatformPrivateConfig.patch({
                    emails: arr,
                }),
            }));
        }

        await pop({ force: true });
        saving.value = false;
    }
    catch (e) {
        console.error(e);
        errors.errorBox = new ErrorBox(e);
        saving.value = false;
    }
}

async function save() {
    if (saving.value) {
        return;
    }
    saving.value = true;

    if (!await errors.validator.validate()) {
        saving.value = false;
        return;
    }
    try {
        if (organization.value) {
            // First save emails
            const organizationPatch = organizationManager.value.getPatch().patch({
                privateMeta: OrganizationPrivateMetaData.patch({
                    emails: patch.value,
                }),
            });

            for (const webshop of webshops.value) {
                // Check if changed
                const prev = webshop.webshop.privateMeta !== null && webshop.webshop.privateMeta.defaultEmailId !== null && webshop.webshop.privateMeta.defaultEmailId === props.email.id;
                if (prev !== webshop.selected) {
                    organizationPatch.webshops.addPatch(WebshopPreview.patch({
                        id: webshop.webshop.id,
                        privateMeta: WebshopPrivateMetaData.patch({
                            defaultEmailId: webshop.selected ? props.email.id : null,
                        }),
                    }));
                }
            }

            // Save
            await organizationManager.value.patch(organizationPatch, { owner, shouldRetry: false });

            // Clear patch
            patch.value = new PatchableArray();

            // Save period
            let shouldSavePeriod = false;
            const organizationPeriodPatch = OrganizationRegistrationPeriod.patch({ id: organization.value?.period.id });
            for (const group of groups.value) {
                // Check if changed
                const prev = group.group.privateSettings !== null && group.group.privateSettings.defaultEmailId !== null && group.group.privateSettings.defaultEmailId === props.email.id;
                if (prev !== group.selected) {
                    organizationPeriodPatch.groups.addPatch(Group.patch({
                        id: group.group.id,
                        privateSettings: GroupPrivateSettings.patch({
                            defaultEmailId: group.selected ? props.email.id : null,
                        }),
                    }));
                    shouldSavePeriod = true;
                }
            }

            if (shouldSavePeriod) {
                await patchOrganizationPeriod(organizationPeriodPatch);
            }
        }
        else {
            await platformManager.value.patch(Platform.patch({
                privateConfig: PlatformPrivateConfig.patch({
                    emails: patch.value,
                }),
            }));
        }

        await pop({ force: true });
        saving.value = false;
    }
    catch (e) {
        console.error(e);
        errors.errorBox = new ErrorBox(e);
        saving.value = false;
    }
}

const hasChangesFull = computed(() => {
    if (!hasChanges.value) {
        let otherChanges = false;
        for (const group of groups.value) {
            // Check if changed
            const prev = group.group.privateSettings !== null && group.group.privateSettings.defaultEmailId !== null && group.group.privateSettings.defaultEmailId === props.email.id;
            if (prev !== group.selected) {
                otherChanges = true;
                break;
            }
        }

        for (const webshop of webshops.value) {
            // Check if changed
            const prev = webshop.webshop.privateMeta !== null && webshop.webshop.privateMeta.defaultEmailId !== null && webshop.webshop.privateMeta.defaultEmailId === props.email.id;
            if (prev !== webshop.selected) {
                otherChanges = true;
                break;
            }
        }

        return otherChanges;
    }

    return true;
});

const shouldNavigateAway = async () => {
    if (!hasChangesFull.value) {
        return true;
    }
    return await CenteredMessage.confirm($t('996a4109-5524-4679-8d17-6968282a2a75'), $t('106b3169-6336-48b8-8544-4512d42c4fd6'));
};

defineExpose({
    shouldNavigateAway,
});

</script>
